/**
 * SINGLE SOURCE OF CONTENT
 * ------------------------------------------------------------------
 * Edit headlines, proof points, contact details, qualification wording,
 * FAQs, form options, CTA labels and media URLs here — without touching
 * component code. Runtime URLs (prospectus / calendar / analytics) are
 * loaded from the backend /api/config so they can be changed server-side.
 */

export const brand = {
  name: "Operations Transformation Program",
  shortName: "OTP",
  partnerLine: "Training delivered & assessed by Axiom College · RTO 40489",
  tagline: "Transform. Apply. Qualify.",
  masterMessage:
    "Transformation is a skill. This program teaches it, you apply it, and the qualification proves you did.",
  positioningPrinciple:
    "The qualification validates the transformation. It does not define it.",
  complianceLine:
    "Delivered and assessed by Axiom College (RTO 40489) as part of Axiom College's training services.",
  phone: "1800 114 419",
};

export const media = {
  logo: "/axiom-logo.png",
  logoWhite: "/axiom-logo-white.png",
  hero: "https://static.prod-images.emergentagent.com/jobs/beefd3f4-e62e-4dd9-a0ba-b118855fc9a0/images/0680a52b19cb5ced346f3ba7e6b3efc70abc8deca7487a0bc129a26fe122d5b1.png",
  // Optional muted autoplay video (env-replaceable). When set, it overrides the montage.
  heroVideo: "",
  // Optional sound-on conversion film (Version 2). When set, a "Watch the film" button appears and opens a modal.
  heroFilmUrl: "https://vimeo.com/1211630781",
  // Poster still shown on the hero film card (frame pulled from the film itself).
  heroFilmPoster: "/film-poster.jpg",
  heroPoster: "https://static.prod-images.emergentagent.com/jobs/beefd3f4-e62e-4dd9-a0ba-b118855fc9a0/images/0680a52b19cb5ced346f3ba7e6b3efc70abc8deca7487a0bc129a26fe122d5b1.png",
  // Cinematic montage — narrative arc: recurring problem -> the system -> real tools -> transformed operation.
  heroScenes: [
    { url: "https://static.prod-images.emergentagent.com/jobs/beefd3f4-e62e-4dd9-a0ba-b118855fc9a0/images/f0466f0fb0f120e4aa5706f3766c2b55ed7f77994e8974a64f2ccb312c8aa00d.png", label: "ANOTHER SHIFT. SAME PROBLEMS." },
    { url: "https://static.prod-images.emergentagent.com/jobs/beefd3f4-e62e-4dd9-a0ba-b118855fc9a0/images/9d0c1c14d2e4ff01ce3116a42257121b2760c275fade5a8482f08b0a9c31150f.png", label: "SAME MISSED TARGETS. STALLED PRODUCTION." },
    { url: "https://static.prod-images.emergentagent.com/jobs/beefd3f4-e62e-4dd9-a0ba-b118855fc9a0/images/802357817000aef875fc3615a1b5bd77ad8b81af46c429d157c1ce7ca37f996d.png", label: "SAME BROKEN HANDOVERS." },
    { url: "https://static.prod-images.emergentagent.com/jobs/beefd3f4-e62e-4dd9-a0ba-b118855fc9a0/images/6779d3ac5143847143435e358437a2acd59d0a5065e781e24e70198549004527.png", label: "THE PROBLEM ISN'T EFFORT. IT'S THE SYSTEM." },
    { url: "https://static.prod-images.emergentagent.com/jobs/beefd3f4-e62e-4dd9-a0ba-b118855fc9a0/images/a042a3ce598585c61ba24a9ea6fcc4c3822b3020de027de864f07a868c364775.png", label: "REAL TOOLS. DIRECT WORKPLACE APPLICATION." },
    { url: "https://static.prod-images.emergentagent.com/jobs/beefd3f4-e62e-4dd9-a0ba-b118855fc9a0/images/0680a52b19cb5ced346f3ba7e6b3efc70abc8deca7487a0bc129a26fe122d5b1.png", label: "CLARITY TO ACT. CAPABILITY TO DELIVER." },
  ],
  problem: "https://static.prod-images.emergentagent.com/jobs/beefd3f4-e62e-4dd9-a0ba-b118855fc9a0/images/9d0c1c14d2e4ff01ce3116a42257121b2760c275fade5a8482f08b0a9c31150f.png",
  simulation: "https://static.prod-images.emergentagent.com/jobs/beefd3f4-e62e-4dd9-a0ba-b118855fc9a0/images/6779d3ac5143847143435e358437a2acd59d0a5065e781e24e70198549004527.png",
  finalCta: "https://static.prod-images.emergentagent.com/jobs/beefd3f4-e62e-4dd9-a0ba-b118855fc9a0/images/0680a52b19cb5ced346f3ba7e6b3efc70abc8deca7487a0bc129a26fe122d5b1.png",
  personas: "https://static.prod-images.emergentagent.com/jobs/beefd3f4-e62e-4dd9-a0ba-b118855fc9a0/images/a042a3ce598585c61ba24a9ea6fcc4c3822b3020de027de864f07a868c364775.png",
};

export const nav = [
  { label: "The System", id: "system" },
  { label: "Outcomes", id: "outcomes" },
  { label: "Simulation", id: "simulation" },
  { label: "Tools", id: "tools" },
  { label: "Qualification", id: "qualification" },
  { label: "Enrol", id: "enrol" },
  { label: "FAQs", id: "faqs" },
];

export const cta = {
  prospectus: "Request the 2026 Prospectus",
  prospectusShort: "Request the Prospectus",
  consultation: "Book your Pre-Training Review",
  exploreSystem: "See how the program works",
};

export const hero = {
  eyebrow: "FOR LEADERS LOOKING TO TAKE THEIR ORGANISATION TO THE NEXT LEVEL",
  headlineLines: ["Stop firefighting.", "Build the system that", "transforms performance."],
  supporting:
    "A practitioner-built operations transformation program combining field-tested frameworks, an operationally realistic mining simulation and tools you can deploy directly in the workplace.",
  proofLine:
    "Built on the lead practitioner's 16 years of consulting IP · 40+ prior transformations · Real tools · Applied simulation",
  qualificationNote:
    "You complete the nationally recognised BSB50420 Diploma of Leadership and Management — training and assessment delivered, assessed and issued by Axiom College (RTO 40489). Completion of the program does not guarantee competency. This program is not offered to overseas students on an Australian student visa.",
};

export const proofStats = [
  { value: "16", suffix: "yrs", label: "Lead practitioner's consulting IP" },
  { value: "40+", suffix: "", label: "Prior transformations (lead practitioner)" },
  { value: "3", suffix: "", label: "Transformation phases" },
  { value: "9+1", suffix: "", label: "Core modules plus a dedicated AI module" },
  { value: "12", suffix: "", label: "Qualification units mapped" },
];

export const problem = {
  eyebrow: "The Operating Problem",
  heading:
    "Transformation does not fail because organisations lack initiatives. It fails because they lack a system.",
  intro:
    "Most transformation programs struggle to sustain results. The symptoms are recognisable in almost any complex operation.",
  symptoms: [
    "Constant reactive firefighting",
    "Improvement initiatives that fade",
    "Results that depend on who is on shift",
    "Meetings that report problems rather than solve them",
    "Leaders trapped working in the operation rather than on it",
    "Frameworks that never become daily management disciplines",
  ],
};

export const architecture = {
  eyebrow: "The Transformation System",
  heading: "Lead self. Mobilise the team. Transform the operating system.",
  intro:
    "Three phases, built in sequence. You transform yourself, then your team, then the operating system around them. Each phase builds capability, changes what happens in the workplace, and produces tangible outputs.",
  learningLoop: ["Learn", "Practise", "Use", "Reflect", "Prove"],
  phases: [
    {
      number: "01",
      title: "Lead Self",
      moduleRange: "Modules 1–4",
      idea:
        "Before leaders transform an organisation, they must establish the disciplines required to transform themselves.",
      capability: "Personal discipline, productivity mindset, high-performance habits and structured communication.",
      workplace: "Priorities, leadership rhythm, transformation narrative and personal accountability.",
      outputs: "Personal operating system, communication plan, leadership commitments.",
      modules: [
        {
          code: "M1",
          title: "Productivity and Value Creation",
          desc: "Understand why operational excellence matters and where value is created or lost in a working system.",
          output: "Value logic, KPI structure, personal contribution",
        },
        {
          code: "M2",
          title: "Leadership Mindset for Change",
          desc: "Build the leadership mindset that makes sustained change possible under real operational pressure.",
          output: "Growth mindset, mental toughness",
        },
        {
          code: "M3",
          title: "Personal Discipline & High-Performance Habits",
          desc: "Establish the personal disciplines and routines that hold performance steady across shifts and weeks.",
          output: "Habit system, compounding routines",
        },
        {
          code: "M4",
          title: "Structured Communication & Mobilisation",
          desc: "Structure your communications and keep communicating so intent survives contact with the operation.",
          output: "Communication plan, Commitment Curve",
        },
      ],
    },
    {
      number: "02",
      title: "Mobilise the Team",
      moduleRange: "Modules 5–7A",
      idea:
        "Build leadership, problem-solving, execution and AI-augmented capability across the team.",
      capability: "Team leadership, structured problem solving, Leader Standard Work, execution discipline and AI-enabled work.",
      workplace: "Pre-starts, handovers, performance dialogues, coaching, line walks and team development.",
      outputs: "Leader Standard Work, problem-solving packs, meeting cadence, team capability plans.",
      modules: [
        {
          code: "M5",
          title: "Build & Lead High-Performance Teams",
          desc: "Attract, develop and retain the people an operation depends on, and lead them at scale.",
          output: "Adaptive leadership, Influence Model, development plans",
        },
        {
          code: "M6",
          title: "Structured Problem Solving",
          desc: "Apply structured problem-solving so the team fixes root causes instead of managing symptoms.",
          output: "Issue trees, root cause, countermeasures",
        },
        {
          code: "M7",
          title: "Leader Standard Work & Execution Discipline",
          desc: "Prioritise the work that matters and standardise how it is executed for consistent results.",
          output: "Leader Standard Work, input KPIs, meeting cadence",
        },
        {
          code: "M7A",
          title: "AI for Transformation & Decision Support",
          desc: "Use AI as a transformation accelerator across analysis, communication and decision support — not as a novelty.",
          output: "Prompt library, applied AI workflows",
        },
      ],
    },
    {
      number: "03",
      title: "Transform the Operating System",
      moduleRange: "Modules 8–9",
      idea:
        "Diagnose an entire operating system, identify value, establish the Transformation Office and govern delivery through to sustained results.",
      capability: "Organisational diagnosis, value identification, roadmap design, governance and delivery.",
      workplace: "Site diagnostic, opportunity pipeline, initiative delivery, Transformation Office and benefits tracking.",
      outputs: "Diagnostic heatmap, roadmap, initiative charters, governance system, benefits tracker.",
      modules: [
        {
          code: "M8",
          title: "Diagnose the Operating System",
          desc: "Diagnose an entire operating system, quantify the value at stake and build a defensible roadmap.",
          output: "Diagnostic, opportunity register, roadmap",
        },
        {
          code: "M9",
          title: "Mobilise & Deliver Transformation",
          desc: "Establish the Transformation Office and govern delivery through to sustained operational results.",
          output: "Transformation Office, governance, benefits",
        },
      ],
    },
  ],
};

export const outcomes = {
  eyebrow: "Practical Outcomes",
  heading: "Capability you can deploy on Monday — not a certificate of attendance.",
  items: [
    {
      title: "A complete transformation methodology",
      desc: "Walk into a dysfunctional operation and know exactly how to structure the work — from first diagnosis to sustained result.",
    },
    {
      title: "Leader Standard Work and operating cadence",
      desc: "Install the daily and weekly management disciplines that hold performance steady regardless of who is on shift.",
    },
    {
      title: "Structured problem-solving capability",
      desc: "Lead teams to root cause and durable countermeasures instead of recurring firefighting.",
    },
    {
      title: "Organisational diagnostic and roadmapping tools",
      desc: "Assess an operating system, quantify the value at stake and build a prioritised, defensible roadmap.",
    },
    {
      title: "Transformation Office and governance capability",
      desc: "Stand up the governance, cadence and reporting that keep a transformation on track to results.",
    },
    {
      title: "AI-augmented analysis and decision support",
      desc: "Apply AI to accelerate diagnosis, sharpen communication and support faster, better operational decisions.",
    },
  ],
};

export const simulation = {
  eyebrow: "Applied Learning",
  heading: "Apply the system before the stakes are real.",
  quote:
    "Theory without application is entertainment. Application without evidence is anecdote.",
  body: [
    "Meridian Lithium Operations is a fictional but operationally realistic mining organisation that runs across the program.",
    "You work real artefacts — reports, transcripts and data — the way a transformation consultant would. The simulation turns frameworks into judgement, and judgement into a portfolio of evidence.",
  ],
  dailyReport: {
    title: "Meridian Lithium — Daily Operating Report",
    shift: "Shift A",
    note: "Illustrative simulation data",
    kpis: [
      { label: "Safety", value: "0 recordable", tone: "good" },
      { label: "Tonnes moved vs plan", value: "92%", tone: "warn" },
      { label: "Plant throughput", value: "96%", tone: "good" },
      { label: "Recovery", value: "On plan", tone: "good" },
      { label: "Asset availability", value: "81%", tone: "warn" },
      { label: "Unplanned downtime", value: "Elevated", tone: "bad" },
      { label: "Cost per tonne", value: "Adverse", tone: "bad" },
    ],
  },
  evidenceToCapability: {
    heading: "From evidence to capability",
    steps: [
      { n: "01", label: "Evidence, reports, transcripts, data" },
      { n: "02", label: "Diagnosis, issue tree and analysis" },
      { n: "03", label: "Root cause, the real constraint" },
      { n: "04", label: "Recommendation and roadmap" },
      { n: "05", label: "Implementation and governance" },
      { n: "06", label: "Evidence of capability" },
    ],
  },
  vignette: {
    tag: "Worked vignette · Haul-truck queue",
    body: "A queue at the excavator looks like a truck problem. In the simulation, participants trace it through dispatch, planning, maintenance, handover and leadership routines, and find the real constraint. The symptom becomes a systemic diagnosis, then a validated opportunity, then banked value.",
  },
};

export const toolkit = {
  eyebrow: "Representative Toolkit",
  heading: "Real tools. Direct workplace application.",
  intro:
    "You leave with a working toolkit, not a reading list. Each tool solves a specific operating problem and produces a tangible output you can use on Monday.",
  tools: [
    { title: "Leader Standard Work", desc: "A daily and weekly leadership rhythm." },
    { title: "Shift-handover structure", desc: "No critical information lost between shifts." },
    { title: "Performance-dialogue board", desc: "Meetings that resolve, not just report." },
    { title: "Structured problem solving", desc: "Define, analyse, solve, sustain." },
    { title: "Issue tree", desc: "Separate the real constraint from the symptom." },
    { title: "Diagnostic maturity heatmap", desc: "See where the operating system is weak." },
    { title: "Stakeholder interview guide", desc: "Surface the real issues quickly." },
    { title: "Opportunity register", desc: "Link operating levers to value." },
    { title: "Transformation roadmap", desc: "Prioritised, sequenced, resourced." },
    { title: "Initiative charter", desc: "Scope, owner, benefits logic." },
    { title: "Transformation Office governance", desc: "The cadence that sustains delivery." },
    { title: "Benefits tracker & AI prompt library", desc: "Bank value; accelerate the work." },
  ],
  heatmap: {
    title: "Operations transformation diagnostic — maturity heatmap",
    note: "Meridian Lithium Operations · illustrative simulation data",
    scale: "1 Low — 5 High",
    caption: "Drag the operating system from reactive firefighting to a governed, self-sustaining system.",
    dimensions: [
      { label: "Leadership & planning", before: 2, after: 5 },
      { label: "Performance management", before: 2, after: 4 },
      { label: "Problem solving & work mgmt", before: 3, after: 5 },
      { label: "Governance & sustainability", before: 1, after: 4 },
    ],
  },
};

export const trainingSample = {
  eyebrow: "See the material",
  heading: "See the quality of our training material.",
  intro:
    "Take a look inside. This free Meeting Management guide is drawn straight from our program library — the same practical, workplace-ready standard you'll find throughout the Operations Transformation Program.",
  bullets: [
    "A clear decision flow for when a meeting is actually needed — and who truly needs to be there.",
    "The '5 Ps' preparation model, with a ready-to-use meeting template.",
    "The RACI framework for locking in roles, decisions and accountability.",
    "Action-capture techniques and the '7 deadly meeting sins' to avoid.",
  ],
  meta: "19-page guide · flowcharts, checklists & templates · PDF",
  image: "/meeting-5ps-preview.png",
  formTitle: "Get the free sample",
  formNote: "Enter your details and we'll unlock the download instantly.",
  cta: "Unlock the free download",
  downloadCta: "Download the Meeting Management guide",
  successTitle: "Here's your sample.",
  successBody: "Enjoy the guide — this is the standard of material inside the full program. We've also emailed you a copy.",
  privacy: "We'll email you a copy and occasional program updates. Unsubscribe anytime.",
};


export const differentiation = {
  eyebrow: "A Category of One",
  heading: "Built differently because it serves a different purpose.",
  intro:
    "A premium, practitioner-built operations transformation system that combines consulting-grade methodology, applied learning and a nationally recognised qualification delivered and assessed by Axiom College.",
  columns: ["Operations Transformation Program", "Generic leadership training", "Conventional diploma delivery"],
  rows: [
    { criterion: "Source of content", otp: "The lead practitioner's consulting IP", generic: "Theory and models", diploma: "Built around qualification units" },
    { criterion: "Operational application", otp: "Every framework applied to a live operation", generic: "Classroom discussion", diploma: "Assignment-based" },
    { criterion: "Integrated methodology", otp: "Nine interdependent modules, one system", generic: "Standalone topics", diploma: "Units of competency" },
    { criterion: "Simulation", otp: "Meridian Lithium Operations engagement", generic: "None", diploma: "None" },
    { criterion: "Evidence method", otp: "Applied portfolio of transformation work", generic: "Attendance", diploma: "Written assessment" },
    { criterion: "Qualification", otp: "BSB50420 assessed during the program", generic: "Certificate of attendance", diploma: "Diploma on completion" },
    { criterion: "AI application", otp: "Dedicated, business-applied AI module", generic: "Rare or theoretical", diploma: "Not addressed" },
    { criterion: "Focus on sustained results", otp: "Governed through to operational outcomes", generic: "Individual insight", diploma: "Credential attainment" },
  ],
  pillars: [
    { title: "Practitioner-built IP", desc: "Frameworks distilled from the lead practitioner's 16 years of global consulting across heavy industry — field-proven, not academic theory." },
    { title: "Deliberate transformation architecture", desc: "A three-phase system where each module builds on the last." },
    { title: "Academic enrichment", desc: "Enriched with academic content and case studies that deepen — without overriding — the consulting IP." },
    { title: "Practical AI integration", desc: "A dedicated module applying AI as a transformation accelerator across analysis and delivery." },
    { title: "Nationally recognised qualification", desc: "You complete the BSB50420 Diploma of Leadership and Management — delivered, assessed and issued by Axiom College — with competency assessed during the program." },
  ],
};

export const personas = {
  eyebrow: "Who It's For",
  heading: "Built for people responsible for real performance.",
  roles: [
    {
      key: "technical",
      label: "Technical Managers & Superintendents",
      challenge: "Drowning in reactive firefighting, with team performance that depends on who is on shift.",
      want: "To standardise and elevate team performance and build a system that runs without constant intervention.",
      benefit: "A complete transformation methodology plus Leader Standard Work you can install directly in your operation.",
      message: "Stop firefighting. Build the system that transforms your operation — and hold credible evidence that you can lead at scale.",
    },
    {
      key: "exco",
      label: "Managing Directors & EXCO",
      challenge: "Culture and results are not shifting despite investment, and transformation initiatives fail to sustain.",
      want: "To embed the right mindsets, behaviours and habits and build a self-sustaining transformation culture.",
      benefit: "Transformation Office and governance capability to make capability-building core to strategy.",
      message: "Embed the transformation system your organisation needs — not another workshop that fades in a week.",
    },
    {
      key: "consultant",
      label: "Management Consultants",
      challenge: "Client work demands structured methodology and rigour beyond generic frameworks.",
      want: "A comprehensive, deployable transformation toolkit and deeper credibility.",
      benefit: "Sixteen years of transformation IP, systematised into a methodology you can deploy.",
      message: "Your capability-building practice, systematised — with the credential to match.",
    },
    {
      key: "coach",
      label: "Business Coaches",
      challenge: "Coaching conversations lack operational depth, making it hard to move clients from insight to implementation.",
      want: "A proven system to embed and sustain the changes coaching initiates.",
      benefit: "Structured problem-solving and operating cadence that make your coaching stick.",
      message: "Move your clients from conversation to transformation with an operational system behind you.",
    },
    {
      key: "founder",
      label: "Entrepreneurs & Business Owners",
      challenge: "Operations are growing faster than systems, and everything depends on the founder.",
      want: "To build the operational backbone for sustainable growth.",
      benefit: "Diagnostic and roadmapping tools to build the operating system your business needs to scale.",
      message: "Build the operating system your business needs to scale — starting with you.",
    },
    {
      key: "professional",
      label: "Ambitious Professionals",
      challenge: "Generic qualifications do not differentiate in a competitive market.",
      want: "Practical skills that apply immediately, backed by real-world depth.",
      benefit: "Learn how global consultants transform operations — with evidence of the capability.",
      message: "Learn the system global operators use to transform performance — and prove that you can apply it.",
    },
  ],
};

export const qualification = {
  eyebrow: "The Qualification",
  heading: "A nationally recognised qualification, delivered and assessed by Axiom College.",
  positioning:
    "You complete the nationally recognised BSB50420 Diploma of Leadership and Management. The training and assessment are delivered by Axiom College (RTO 40489), which issues the qualification. Competency is assessed during the program through direct assessment methods — knowledge testing against defined benchmarks, performance tasks aligned to the performance criteria, and direct observation and projects — mapped to every unit's requirements.",
  facts: [
    "BSB50420 Diploma of Leadership and Management",
    "AQF Level 5",
    "12 units of competency",
    "Training delivered, assessed and issued by Axiom College",
    "RTO number 40489",
    "Assessed during the program through direct assessment methods",
    "Mapped to every Element, Performance Criteria, Performance & Knowledge Evidence, Foundation Skill and Assessment Condition",
    "Final competency decisions made by a qualified Axiom College assessor",
  ],
  callout: "No conventional exams. Competency is assessed through knowledge testing, performance tasks and direct observation as you complete the program.",
  unitsHeading: "BSB50420 (Release 3) — twelve units of competency",
  unitsNote: "Six core and six elective units, aligned to BSB50420 Release 3 (current from 25 January 2022). These twelve units are fixed and cannot be interchanged. Assessment against these units is conducted during the program by Axiom College (RTO 40489).",
  units: [
    { code: "BSBCMM511", title: "Communicate with influence", type: "Core" },
    { code: "BSBCRT511", title: "Develop critical thinking in others", type: "Core" },
    { code: "BSBLDR523", title: "Lead and manage effective workplace relationships", type: "Core" },
    { code: "BSBOPS502", title: "Manage business operational plans", type: "Core" },
    { code: "BSBPEF502", title: "Develop and use emotional intelligence", type: "Core" },
    { code: "BSBTWK502", title: "Manage team effectiveness", type: "Core" },
    { code: "BSBCMM412", title: "Lead difficult conversations", type: "Elective" },
    { code: "BSBCRT512", title: "Originate and develop concepts", type: "Elective" },
    { code: "BSBLDR522", title: "Manage people performance", type: "Elective" },
    { code: "BSBPEF501", title: "Manage personal and professional development", type: "Elective" },
    { code: "BSBSTR501", title: "Establish innovative work environments", type: "Elective" },
    { code: "BSBSTR502", title: "Facilitate continuous improvement", type: "Elective" },
  ],
  disclaimer:
    "The training and assessment for the BSB50420 Diploma of Leadership and Management are delivered by Axiom College (RTO 40489), which issues the qualification. Competency is assessed during the program and is not automatic or guaranteed; completion of the program alone does not guarantee competency. Final competency decisions are made by a qualified Axiom College assessor (RTO 40489).",
};

export const howItWorks = {
  eyebrow: "How It Works",
  heading: "A clear path from interest to a nationally recognised qualification.",
  glance: [
    { icon: "clock", value: "9–12 months", label: "Guided, self-paced duration" },
    { icon: "gauge", value: "4–6 hrs / week", label: "Typical time commitment" },
    { icon: "video", value: "Fortnightly", label: "Fully online live sessions" },
    { icon: "repeat", value: "Monthly", label: "Rolling intakes" },
    { icon: "laptop", value: "Fully online", label: "Delivered online + workplace application" },
    { icon: "clipboard", value: "During the program", label: "Assessment against BSB50420 by Axiom College" },
  ],
  steps: [
    { n: "01", title: "Request the prospectus", desc: "Tell us about your role and what you need to transform." },
    { n: "02", title: "Complete your Pre-Training Review", desc: "A documented review covering workplace access, prior experience and an LLN self-assessment, to confirm the program is right for you or your organisation." },
    { n: "03", title: "Confirm your fit and enrol", desc: "A focused conversation confirms the program is right for you or your organisation, then you secure your place." },
    { n: "04", title: "Work through the system and simulation", desc: "Apply every framework across the Meridian Lithium Operations simulation." },
    { n: "05", title: "Complete assessment during the program", desc: "Competency is assessed by Axiom College through knowledge testing, performance tasks and direct observation as you work." },
  ],
};

export const faqs = [
  {
    q: "Is this a nationally recognised qualification?",
    a: "Yes. You complete the nationally recognised BSB50420 Diploma of Leadership and Management. The training and assessment are delivered by Axiom College (RTO 40489), which issues the qualification.",
  },
  {
    q: "Is this a conventional diploma course?",
    a: "No. This is not classroom lectures and end-of-term exams. The Operations Transformation Program leads to the nationally recognised BSB50420 Diploma of Leadership and Management through practical, applied training. You learn the Operations Transformation system, apply it inside an operationally realistic workplace simulation (Meridian Lithium Operations), and are assessed progressively as you demonstrate competency against each unit. The training and assessment are delivered, assessed and issued by Axiom College (RTO 40489). Completion of the program does not guarantee competency.",
  },
  {
    q: "How is assessment conducted?",
    a: "Assessment is conducted throughout the program through Axiom College's online learning and assessment system. You access your assessment tasks, instructions, submission points, knowledge questions, project activities and observation requirements through this system. Assessment methods include knowledge testing, practical performance tasks, workplace-style projects and direct observation by a qualified Axiom College assessor. Each task is mapped to the relevant unit requirements, including the Elements, Performance Criteria, Performance Evidence, Knowledge Evidence, Foundation Skills and Assessment Conditions. You submit your evidence through the online system unless your assessor advises otherwise. Final competency decisions are made by a qualified Axiom College assessor after all required evidence has been assessed as satisfactory.",
  },
  {
    q: "Are there examinations?",
    a: "There are no conventional written examinations. Competency is achieved by completing a range of assessment tasks that let you demonstrate the required skills and knowledge for each unit. These may include knowledge questions, practical activities, workplace-style projects, simulated business tasks and direct observation by a qualified Axiom College assessor. As you work through the Operations Transformation system and the Meridian Lithium simulation, you generate assessment evidence that shows you can apply the required skills in a practical context. Your assessor reviews this evidence against the unit requirements and provides feedback where needed. A final competency decision is made only after all required evidence has been completed, submitted, reviewed and assessed as satisfactory.",
  },
  {
    q: "How long does the program take?",
    a: "Most participants complete the program within 9 to 12 months, depending on their pace, prior experience, available study time and assessment progress. The program is delivered across three phases and nine modules, including a dedicated AI module, and is mapped to the twelve units of competency in BSB50420 Diploma of Leadership and Management. Each module includes learning activities, applied workplace-style tasks, assessment evidence and opportunities for observation, and is completed in sequence. As a guide, allow approximately four to six weeks per module, although this will vary with your existing skills, confidence and weekly availability. Training is delivered online through Axiom College's learning and assessment system, combining self-directed study, trainer support, practical activities and scheduled assessment requirements. A realistic completion plan is confirmed with you at the Pre-Training Review. This covers how the training is delivered, the access you will need, your expected study commitment, assessment due dates, and how the program fits around your work and personal commitments.",
  },
  {
    q: "Do I need to be currently employed?",
    a: "Access to an operating environment strengthens the applied work and evidence. The Meridian Lithium Operations simulation provides a realistic context regardless of your current role. We will confirm this during your Pre-Training Review.",
  },
  {
    q: "Who is the program designed for?",
    a: "Experienced managers, superintendents, operational leaders, technical professionals, executives and transformation practitioners in mining, metals, energy, heavy industry and other complex operating environments.",
  },
  {
    q: "Can organisations use the program for leadership cohorts?",
    a: "Yes. Organisations can use the program to build transformation capability across a leadership cohort. Discuss cohort delivery during your Pre-Training Review.",
  },
  {
    q: "Is the qualification guaranteed?",
    a: "No. The qualification is not automatic or guaranteed. Completion of the program alone does not guarantee competency, and final competency decisions are made by a qualified Axiom College assessor.",
  },
  {
    q: "What is the Meridian Lithium Operations simulation?",
    a: "It is a fictional but operationally realistic mining transformation simulation that runs across the program. You apply each framework to diagnose issues, make recommendations, build implementation plans and generate a portfolio of practical evidence.",
  },
];

export const finalCta = {
  heading: "Learn the system. Apply it. Prove it.",
  supporting:
    "Request the 2026 Prospectus and discover whether the Operations Transformation Program is the right fit for you or your organisation.",
};

export const learnerInfo = {
  eyebrow: "Before You Enrol",
  heading: "Fees, refunds and your rights as a student.",
  intro:
    "The Operations Transformation Program delivers the nationally recognised BSB50420 Diploma of Leadership and Management. Training and assessment are delivered by Axiom College (RTO 40489), which issues the qualification and receives all fees. Please read the Student Handbook & Program Guide before enrolling — it contains full details of the items below.",
  fee: "A single, all-inclusive fee covers both training and assessment — there are no separate or hidden assessment charges. Axiom does not accept more than $1,500 in prepaid fees before your training starts; the balance is invoiced progressively as you work through the units. Pay by credit card, direct bank transfer, Stripe or PayPal — including a 4-month payment plan.",
  entryNote: "Entry requirements: this program suits leaders and managers in operational roles. You need access to a workplace or operational context to generate the evidence assessed during the program.",
  cricos: "This program is not offered to overseas students on an Australian student visa.",
  items: [
    {
      title: "Entry requirements",
      desc: "Suited to leaders and managers in operational roles. You'll need access to a workplace or operational context throughout the program, because a significant portion of your evidence is drawn from real work situations. If you're between roles or unsure whether your current context is suitable, contact us before enrolling — we'll confirm before you commit.",
      link: { label: "Axiom Policies & Procedures", href: "https://www.axiomcollege.com.au/polices-procedures/" },
    },
    {
      title: "Fees & payment",
      desc: "A single all-inclusive fee covers training and assessment — there are no separate or hidden assessment charges. We accept credit card, direct bank transfer, Stripe or PayPal, and payments can be made via a 4-month payment plan. Where fees are paid in advance, Axiom does not accept more than $1,500 before your training starts; the balance is invoiced progressively as you work through the units.",
      link: { label: "POL11 Fees & Charges Policy", href: "https://www.axiomcollege.com.au/wp-content/uploads/2026/06/POL11_Fees_and_Charges_Policy_V5_2026.pdf" },
    },
    {
      title: "Refunds, cancellation & withdrawal",
      desc: "If you cancel before training starts, you receive a full refund. After training starts, refunds are pro-rata — you keep what you've used and we refund the rest on a sliding scale (up to 70% back within the first three months). Short courses need at least 5 business days' notice to be refundable. Refund requests go to accounts@axiomcollege.com.au and are processed within 30 days of the decision.",
      link: { label: "POL08 Refund Policy", href: "https://www.axiomcollege.com.au/wp-content/uploads/2026/06/POL08_Refund_Policy_V7_2026.pdf" },
    },
    {
      title: "Tuition protection",
      desc: "If Axiom is ever unable to finish delivering your course, you receive a full refund for any units you haven't yet completed, and we help you find another provider to complete your qualification. Your prepaid fees are protected — you won't be out of pocket for training you never received.",
      link: { label: "POL08 Refund Policy", href: "https://www.axiomcollege.com.au/wp-content/uploads/2026/06/POL08_Refund_Policy_V7_2026.pdf" },
    },
    {
      title: "Complaints & appeals",
      desc: "If something isn't right, start by talking to your trainer or the Student Support team — most issues are sorted informally. To lodge it formally, or to appeal an assessment decision, use the Complaints and Appeals Form and send it to compliance@axiomcollege.com.au. We acknowledge within 2 working days and aim to resolve within 30 days. Assessment appeals must be lodged within 28 working days of the decision. Your enrolment continues while the matter is being reviewed — there's no disadvantage for raising a concern.",
      link: { label: "POL07 Complaints & Appeals Policy", href: "https://www.axiomcollege.com.au/wp-content/uploads/2026/06/POL07_Complaints_and_Appeals_Handling_Policy_V4_2026.pdf" },
    },
    {
      title: "LLN, learner support & reasonable adjustment",
      desc: "Every learner has the opportunity, if required, to complete a short LLND check at enrolment, so we can identify any support needs early. Adjustments we routinely offer include extra time on assessments, verbal or oral assessment instead of written, plain-English instructions, assistive technology (text-to-speech, screen readers) and adapted learning materials. Support is reviewed at each new unit and any time your circumstances change. Ask your trainer, BDM or the Student Support team — your information is confidential.",
      link: { label: "POL03 LLND Policy", href: "https://www.axiomcollege.com.au/wp-content/uploads/2026/06/POL03_Language_Literacy_Numeracy_and_Digital_LLND_Policy_V2_2026.pdf" },
    },
    {
      title: "Unique Student Identifier (USI)",
      desc: "You need a USI to be issued any nationally recognised qualification in Australia. If you don't already have one, we help you create it during enrolment at no cost — it takes about 5 minutes. If you already have one, just provide it and we verify it against the USI Registry.",
      link: { label: "FRM-USI-001 USI Privacy Notice", href: "https://www.axiomcollege.com.au/polices-procedures/" },
    },
    {
      title: "Credit transfer & recognition of prior learning",
      desc: "Credit Transfer recognises training you've already completed with another RTO — it's free and applied on the basis of your certified transcript and USI record. Recognition of prior learning is different: it assesses skills you already have from work, life or informal training. Both are assessed before your Training Plan is finalised, so tell us about any prior study or relevant experience at enrolment. Ask for the Credit Transfer Request Form or the RPL Application Kit through your BDM.",
      link: { label: "POL04 RPL & Credit Transfer Policy", href: "https://www.axiomcollege.com.au/wp-content/uploads/2026/06/POL04_RPL_and_Credit_Transfer_Policy_V10_2026.pdf" },
    },
    {
      title: "Trainer & assessor credentials",
      desc: "Your trainer holds the required TAE credentials and current vocational competency for BSB50420 — meaning they're qualified to teach the material and have real, recent industry experience actually doing it. Details of who is delivering your program are available on request.",
    },
    {
      title: "Learner protections",
      desc: "You're protected under Australian Consumer Law, the National VET Regulator Act and the Standards for RTOs. If Axiom cannot complete your training, you receive a refund for undelivered units and we help you continue with another provider. If something goes wrong that we cannot resolve internally, you can escalate to the National Training Complaints Hotline (13 38 73) or the Queensland Office of Fair Trading — all free of charge.",
    },
  ],
  handbookCta: "Read Axiom's Policies & Procedures",
};

export const footer = {
  contact: {
    partner: "Training delivered & assessed by Axiom College",
    rto: "RTO 40489",
    phone: "1800 114 419",
    website: "https://www.axiomcollege.com.au/",
    campuses: "Axiom College campuses (this program is delivered fully online)",
    campusList: "Milton (Brisbane) · Mackay · Hervey Bay · Rockhampton",
    email: "info@axiomcollege.com.au",
    studentHandbookUrl: "https://www.axiomcollege.com.au/polices-procedures/",
  },
  responsibility:
    "Axiom College (RTO 40489) delivers the training, conducts the assessment, issues the BSB50420 Diploma of Leadership and Management, and receives all fees.",
  cricos: "This program is not offered to overseas students on an Australian student visa.",
  disclaimer:
    "The BSB50420 Diploma of Leadership and Management is delivered, assessed and issued by Axiom College (RTO 40489). Competency is assessed during the program and is not automatic or guaranteed; final competency decisions are made by a qualified Axiom College assessor.",
  legal: [
    { label: "Privacy Policy", href: "https://www.axiomcollege.com.au/privacy-policy/" },
    { label: "Terms & Conditions of Use", href: "https://www.axiomcollege.com.au/terms-and-conditions-of-use/" },
    { label: "Policies & Procedures", href: "https://www.axiomcollege.com.au/polices-procedures/" },
  ],
};

export const formConfig = {
  industries: [
    "Mining", "Metals & Processing", "Energy & Utilities", "Oil & Gas",
    "Manufacturing", "Construction & Infrastructure", "Logistics & Transport", "Other",
  ],
  roles: [
    "Technical Manager / Superintendent",
    "Managing Director / EXCO",
    "Management Consultant",
    "Business Coach",
    "Entrepreneur / Business Owner",
    "Ambitious Professional",
  ],
};
